import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Baby,
  BarChart3,
  CalendarRange,
  Check,
  LogIn,
  LogOut,
  NotepadText,
  Pencil,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { PresenceOccupancyChart } from "@/components/charts/PresenceOccupancyChart";
import { ClickToCopyId, ErrorState, LoadingState } from "@/components/common";
import { WalletDateRangeInputs } from "@/components/common/WalletDateRangeInputs";
import {
  DataTable,
  DataTablePrimaryText,
  DataTableTitle,
  buildDataTableLabels,
  createActionsColumn,
  type DataTableColumn,
  type DataTableLoadState,
} from "@/components/table";
import {
  Button,
  Card,
  ConfirmModal,
  HoverPopover,
  EmptyState,
  ToggleGroup,
} from "@/components/ui";
import { housePresenceKeys, houseService } from "@/services/houseService";
import { deviceKeys } from "@/services/deviceService";
import { getCountryName } from "@/lib/locales";
import { CountryFlag } from "@/components/ui/phone";
import { presenceGuestDisplayName } from "@/lib/presenceDisplay";
import { toastApiError } from "@/lib/errorMessages";
import { useAuthStore } from "@/store/authStore";
import { Permission, type HouseCurrentPresenceItem, type HousePresenceDailyBucket, type MarketingConsent } from "@/types";
import { formatDate, formatTime } from "@/lib/datetime";
import { EditHousePresenceModal } from "./EditHousePresenceModal";

import { formatLocalInputDate } from "@/lib/walletDateRange";

const DEFAULT_LOOKBACK_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const SECONDS_PER_MS = 1 / 1000;

type PresenceSortColumn =
  | "device"
  | "guests"
  | "contacts"
  | "nationality"
  | "marketing"
  | "arrival"
  | "departure"
  | "notes"
  | "actions";

function defaultDateRange(): { dateFrom: string; dateTo: string } {
  const dateTo = new Date();
  const dateFrom = new Date();
  dateFrom.setDate(dateFrom.getDate() - DEFAULT_LOOKBACK_DAYS);
  return {
    dateFrom: formatLocalInputDate(dateFrom),
    dateTo: formatLocalInputDate(dateTo),
  };
}

function parsePresenceDateTime(date: string, time: string): Date {
  const normalizedTime = time.length >= 5 ? time.slice(0, 5) : time;
  return new Date(`${date}T${normalizedTime}:00`);
}

function formatPresenceMomentParts(
  date: string,
  time: string,
  includeYear: boolean,
): { dateLabel: string; timeLabel: string } {
  const moment = parsePresenceDateTime(date, time);
  return {
    dateLabel: formatDate(moment, {
      month: "short",
      day: "numeric",
      ...(includeYear ? { year: "numeric" } : { year: undefined }),
    }),
    timeLabel: formatTime(moment),
  };
}

function formatPresenceStayDates(
  arrivalDate: string,
  arrivalTime: string,
  departureDate: string,
  departureTime: string,
): {
  arrivalParts: { dateLabel: string; timeLabel: string };
  departureParts: { dateLabel: string; timeLabel: string };
} {
  const includeYear = arrivalDate.slice(0, 4) !== departureDate.slice(0, 4);
  return {
    arrivalParts: formatPresenceMomentParts(
      arrivalDate,
      arrivalTime,
      includeYear,
    ),
    departureParts: formatPresenceMomentParts(
      departureDate,
      departureTime,
      includeYear,
    ),
  };
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function parseDateStartUtcMs(date: string): number {
  const [year, month, day] = date.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
}

function parsePresenceUtcMs(date: string, time: string): number {
  const [year, month, day] = date.split("-").map(Number);
  const [hours = 0, minutes = 0, seconds = 0] = time.split(":").map(Number);
  return Date.UTC(year, month - 1, day, hours, minutes, seconds);
}

function clipSeconds(
  intervalStartMs: number,
  intervalEndMs: number,
  rangeStartMs: number,
  rangeEndMs: number,
): number {
  const clippedStartMs = Math.max(intervalStartMs, rangeStartMs);
  const clippedEndMs = Math.min(intervalEndMs, rangeEndMs);
  if (clippedStartMs >= clippedEndMs) {
    return 0;
  }
  return Math.floor((clippedEndMs - clippedStartMs) * SECONDS_PER_MS);
}

function occupancyPercentage(
  devicePresenceSeconds: number,
  intervalCapacitySeconds: number,
): number {
  if (intervalCapacitySeconds <= 0) {
    return 0;
  }
  return Math.min(
    100,
    (devicePresenceSeconds / intervalCapacitySeconds) * 100,
  );
}

function dateStringsInRange(dateFrom: string, dateTo: string): string[] {
  const startMs = parseDateStartUtcMs(dateFrom);
  const endMs = parseDateStartUtcMs(dateTo);
  const dates: string[] = [];
  for (let currentMs = startMs; currentMs <= endMs; currentMs += MS_PER_DAY) {
    dates.push(new Date(currentMs).toISOString().slice(0, 10));
  }
  return dates;
}

function buildPresenceMetrics(
  presences: HouseCurrentPresenceItem[],
  range: { dateFrom: string; dateTo: string },
  deviceCount: number,
) {
  const rangeStartMs = parseDateStartUtcMs(range.dateFrom);
  const rangeEndMs = parseDateStartUtcMs(range.dateTo) + MS_PER_DAY;
  const intervalCapacitySeconds = Math.floor(
    (rangeEndMs - rangeStartMs) * SECONDS_PER_MS * deviceCount,
  );

  // Parse each stay once, then distribute into day buckets (not O(days × rows)).
  const intervals = presences.map((presence) => ({
    startMs: parsePresenceUtcMs(presence.arrival_date, presence.arrival_time),
    endMs: parsePresenceUtcMs(
      presence.departure_date,
      presence.departure_time,
    ),
  }));

  const devicePresenceSeconds = intervals.reduce(
    (total, interval) =>
      total +
      clipSeconds(
        interval.startMs,
        interval.endMs,
        rangeStartMs,
        rangeEndMs,
      ),
    0,
  );

  const dates = dateStringsInRange(range.dateFrom, range.dateTo);
  const bucketPresenceSeconds = dates.map(() => 0);
  const dayCapacitySeconds = Math.floor(MS_PER_DAY * SECONDS_PER_MS * deviceCount);

  for (const interval of intervals) {
    const clippedStartMs = Math.max(interval.startMs, rangeStartMs);
    const clippedEndMs = Math.min(interval.endMs, rangeEndMs);
    if (clippedStartMs >= clippedEndMs) {
      continue;
    }

    const firstDayIndex = Math.floor(
      (clippedStartMs - rangeStartMs) / MS_PER_DAY,
    );
    const lastDayIndex = Math.floor(
      (clippedEndMs - 1 - rangeStartMs) / MS_PER_DAY,
    );

    for (
      let dayIndex = firstDayIndex;
      dayIndex <= lastDayIndex && dayIndex < dates.length;
      dayIndex += 1
    ) {
      if (dayIndex < 0) {
        continue;
      }
      const bucketStartMs = rangeStartMs + dayIndex * MS_PER_DAY;
      const bucketEndMs = bucketStartMs + MS_PER_DAY;
      bucketPresenceSeconds[dayIndex] += clipSeconds(
        interval.startMs,
        interval.endMs,
        bucketStartMs,
        bucketEndMs,
      );
    }
  }

  const dailyBuckets: HousePresenceDailyBucket[] = dates.map((date, index) => {
    const presenceSeconds = bucketPresenceSeconds[index] ?? 0;
    return {
      date,
      device_presence_seconds: presenceSeconds,
      interval_capacity_seconds: dayCapacitySeconds,
      occupancy_percentage: occupancyPercentage(
        presenceSeconds,
        dayCapacitySeconds,
      ),
    };
  });

  return {
    device_count: deviceCount,
    device_presence_seconds: devicePresenceSeconds,
    interval_capacity_seconds: intervalCapacitySeconds,
    occupancy_percentage: occupancyPercentage(
      devicePresenceSeconds,
      intervalCapacitySeconds,
    ),
    presence_count: presences.length,
    daily_buckets: dailyBuckets,
  };
}

function PresenceContactsCell({
  email,
  phone,
}: {
  email?: string;
  phone?: string;
}) {
  const { t } = useTranslation("houses");
  const copyHint = t("detail.presences.copyContactHint");

  if (!email && !phone) {
    return <MissingValue />;
  }

  return (
    <div className="flex min-w-0 flex-col gap-1">
      {email ? (
        <ClickToCopyId
          value={email}
          displayValue={email}
          title={`${email} — ${copyHint}`}
          className="min-w-0 break-words"
        />
      ) : null}
      {phone ? (
        <ClickToCopyId
          value={phone}
          displayValue={phone}
          title={`${phone} — ${copyHint}`}
          className="min-w-0 break-words"
        />
      ) : null}
    </div>
  );
}

function MissingValue() {
  const { t } = useTranslation("houses");
  return (
    <span className="text-surface-400">
      {t("detail.presences.valueUnknown")}
    </span>
  );
}

function consentLabels(
  consent: MarketingConsent | null | undefined,
  t: (key: string) => string,
): string[] {
  if (!consent) {
    return [];
  }

  return [
    consent.consent_marketing ? t("detail.presences.consentMarketing") : null,
    consent.consent_profiling ? t("detail.presences.consentProfiling") : null,
    consent.consent_third_party
      ? t("detail.presences.consentThirdParty")
      : null,
  ].filter((label): label is string => Boolean(label));
}

function PresenceTable({
  title,
  actions,
  preferencesKey,
  presences,
  emptyMessage,
  loadState,
  showCurrentIndicator = false,
  resetToken,
  defaultSortDirection = "desc",
  onEdit,
  onDelete,
}: {
  title: ReactNode;
  actions?: ReactNode;
  preferencesKey: string;
  presences: HouseCurrentPresenceItem[];
  emptyMessage: string;
  loadState?: DataTableLoadState;
  showCurrentIndicator?: boolean;
  resetToken?: string;
  defaultSortDirection?: "asc" | "desc";
  onEdit?: (presence: HouseCurrentPresenceItem) => void;
  onDelete?: (presence: HouseCurrentPresenceItem) => void;
}) {
  const { t, i18n } = useTranslation("houses");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);

  const columns = useMemo<
    DataTableColumn<HouseCurrentPresenceItem, PresenceSortColumn>[]
  >(
    () => [
      {
        id: "device",
        header: t("detail.presences.colDevice"),
        sortKey: "device",
        sortKind: "text",
        getSortValue: (row) => row.device_display_name,
        defaultWidth: 160,
        cell: (presence) => (
          <div className="flex min-w-0 items-center gap-2">
            {showCurrentIndicator && presence.is_current ? (
              <HoverPopover
                content={t("detail.presences.currentIndicatorHint")}
              >
                <span className="inline-flex h-3 w-3 shrink-0 items-center justify-center">
                  <span
                    className="h-2.5 w-2.5 rounded-full bg-primary-500"
                    aria-hidden
                  />
                  <span className="sr-only">
                    {t("detail.presences.currentIndicatorHint")}
                  </span>
                </span>
              </HoverPopover>
            ) : null}
            <DataTablePrimaryText>
              {presence.device_display_name}
            </DataTablePrimaryText>
          </div>
        ),
      },
      {
        id: "guests",
        header: t("detail.presences.colGuests"),
        sortKey: "guests",
        sortKind: "text",
        getSortValue: (row) =>
          presenceGuestDisplayName(
            row.guest_name,
            t("detail.presences.genericGuestName"),
          ),
        defaultWidth: 180,
        cell: (presence) => (
          <div className="flex min-w-0 items-center gap-2">
            <span className="break-words">
              {t("detail.presences.guestsWithCount", {
                name: presenceGuestDisplayName(
                  presence.guest_name,
                  t("detail.presences.genericGuestName"),
                ),
                count: presence.num_people,
              })}
            </span>
            {presence.has_children ? (
              <HoverPopover
                content={t("detail.presences.childrenBadgeHint")}
              >
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-warning-100 text-warning-800">
                  <Baby className="h-3.5 w-3.5" aria-hidden />
                  <span className="sr-only">
                    {t("detail.presences.childrenBadgeHint")}
                  </span>
                </span>
              </HoverPopover>
            ) : null}
          </div>
        ),
      },
      {
        id: "contacts",
        defaultHidden: true,
        header: t("detail.presences.colContacts"),
        sortKey: "contacts",
        sortKind: "text",
        getSortValue: (row) =>
          [
            row.marketing_consent?.email?.trim(),
            row.marketing_consent?.phone?.trim(),
          ]
            .filter(Boolean)
            .join(" "),
        defaultWidth: 180,
        cell: (presence) => (
          <PresenceContactsCell
            email={presence.marketing_consent?.email?.trim()}
            phone={presence.marketing_consent?.phone?.trim()}
          />
        ),
      },
      {
        id: "nationality",
        header: t("detail.presences.colNationality"),
        sortKey: "nationality",
        sortKind: "text",
        getSortValue: (row) => row.nationality ?? "",
        defaultWidth: 140,
        cell: (presence) =>
          presence.nationality ? (
            <div className="flex min-w-0 items-center gap-2">
              <CountryFlag country={presence.nationality} />
              <span className="break-words">
                {getCountryName(presence.nationality, i18n.language)}
              </span>
            </div>
          ) : (
            <MissingValue />
          ),
      },
      {
        id: "marketing",
        defaultHidden: true,
        header: t("detail.presences.colMarketing"),
        sortKey: "marketing",
        sortKind: "number",
        getSortValue: (row) => consentLabels(row.marketing_consent, t).length,
        defaultWidth: 110,
        cell: (presence) => {
          const grantedConsents = consentLabels(presence.marketing_consent, t);
          return grantedConsents.length > 0 ? (
            <HoverPopover
              content={
                <div className="space-y-1 text-left">
                  <div>{t("detail.presences.consentGrantedHint")}</div>
                  <ul className="list-disc pl-4">
                    {grantedConsents.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                </div>
              }
              contentClassName="max-w-sm whitespace-normal font-normal"
            >
              <span className="inline-flex items-center gap-1 text-success-700">
                <Check className="h-4 w-4" aria-hidden />
                <span>({grantedConsents.length})</span>
                <span className="sr-only">
                  {t("detail.presences.consentGrantedHint")}
                </span>
              </span>
            </HoverPopover>
          ) : (
            <HoverPopover content={t("detail.presences.consentDeniedHint")}>
              <span className="inline-flex text-error-600">
                <X className="h-4 w-4" aria-hidden />
                <span className="sr-only">
                  {t("detail.presences.consentDeniedHint")}
                </span>
              </span>
            </HoverPopover>
          );
        },
      },
      {
        id: "arrival",
        header: t("detail.presences.colArrival"),
        sortKey: "arrival",
        sortKind: "text",
        getSortValue: (row) => `${row.arrival_date}T${row.arrival_time}`,
        defaultWidth: 160,
        cell: (presence) => {
          const stayDates = formatPresenceStayDates(
            presence.arrival_date,
            presence.arrival_time,
            presence.departure_date,
            presence.departure_time,
          );
          return (
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-success-50 text-success-700">
                <LogIn className="h-3.5 w-3.5" aria-hidden />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-surface-800">
                  {stayDates.arrivalParts.dateLabel}
                </span>
                <span className="block text-xs tabular-nums text-surface-500">
                  {stayDates.arrivalParts.timeLabel}
                </span>
              </span>
            </div>
          );
        },
      },
      {
        id: "departure",
        header: t("detail.presences.colDeparture"),
        sortKey: "departure",
        sortKind: "text",
        getSortValue: (row) => `${row.departure_date}T${row.departure_time}`,
        defaultWidth: 160,
        cell: (presence) => {
          const stayDates = formatPresenceStayDates(
            presence.arrival_date,
            presence.arrival_time,
            presence.departure_date,
            presence.departure_time,
          );
          return (
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-warning-50 text-warning-700">
                <LogOut className="h-3.5 w-3.5" aria-hidden />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-surface-800">
                  {stayDates.departureParts.dateLabel}
                </span>
                <span className="block text-xs tabular-nums text-surface-500">
                  {stayDates.departureParts.timeLabel}
                </span>
              </span>
            </div>
          );
        },
      },
      {
        id: "notes",
        header: t("detail.presences.colNotes"),
        sortKey: "notes",
        sortKind: "text",
        getSortValue: (row) => row.notes?.trim() ?? "",
        defaultWidth: 80,
        cell: (presence) => {
          const notes = presence.notes?.trim();
          if (!notes) return null;
          return (
            <HoverPopover
              content={notes}
              contentClassName="max-w-sm whitespace-pre-wrap font-normal normal-case"
            >
              <NotepadText
                className="h-4 w-4 text-surface-500"
                aria-label={t("detail.presences.notesAria")}
              />
            </HoverPopover>
          );
        },
      },
      ...(onEdit || onDelete
        ? [
            createActionsColumn<HouseCurrentPresenceItem, PresenceSortColumn>(
              t("detail.presences.colActions"),
              {
                defaultWidth: onEdit && onDelete ? 96 : 72,
                cell: (presence) => (
                  <div className="inline-flex items-center justify-end gap-0.5">
                    {onEdit ? (
                      <HoverPopover content={t("detail.presences.editAction")}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          aria-label={t("detail.presences.editAction")}
                          onClick={() => onEdit(presence)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </HoverPopover>
                    ) : null}
                    {onDelete ? (
                      <HoverPopover content={t("detail.presences.deleteAction")}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-error-600 hover:bg-error-50 hover:text-error-700"
                          aria-label={t("detail.presences.deleteAction")}
                          onClick={() => onDelete(presence)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </HoverPopover>
                    ) : null}
                  </div>
                ),
              },
            ),
          ]
        : []),
    ],
    [i18n.language, onDelete, onEdit, showCurrentIndicator, t],
  );

  return (
    <DataTable
      title={title}
      actions={actions}
      labels={tableLabels}
      closeLabel={tCommon("close")}
      loadingLabel={tCommon("loading")}
      rows={presences}
      columns={columns}
      getRowKey={(row) => row.id}
      defaultSortColumn="arrival"
      defaultSortDirection={defaultSortDirection}
      preferencesKey={preferencesKey}
      resetToken={resetToken}
      loadState={loadState}
      emptyMessage={emptyMessage}
    />
  );
}

interface HousePresencesSectionProps {
  houseId: string;
  deviceCount: number;
}

export function HousePresencesSection({
  houseId,
  deviceCount,
}: HousePresencesSectionProps) {
  const { t } = useTranslation("houses");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const canEditPresences = hasPermission(Permission.DEVICE_PRESENCES_UPDATE);
  const canDeletePresences = hasPermission(Permission.DEVICE_PRESENCES_DELETE);
  const [editingPresence, setEditingPresence] =
    useState<HouseCurrentPresenceItem | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<HouseCurrentPresenceItem | null>(null);
  const initialRange = useMemo(() => defaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState(initialRange.dateFrom);
  const [dateTo, setDateTo] = useState(initialRange.dateTo);
  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [staysView, setStaysView] = useState<"current" | "upcoming">("current");

  const {
    data: houseDevices = [],
  } = useQuery({
    queryKey: deviceKeys.byHouse(houseId),
    queryFn: () => houseService.listDevices(houseId),
    enabled: !!houseId,
  });

  const {
    data: currentPresences = [],
    isLoading: isLoadingCurrent,
    error: currentError,
    refetch: refetchCurrent,
  } = useQuery({
    queryKey: housePresenceKeys.current(houseId),
    queryFn: () => houseService.listCurrentPresences(houseId),
  });

  const {
    data: upcomingPresences = [],
    isLoading: isLoadingUpcoming,
    error: upcomingError,
    refetch: refetchUpcoming,
  } = useQuery({
    queryKey: housePresenceKeys.upcoming(houseId),
    queryFn: () => houseService.listUpcomingPresences(houseId),
  });

  const {
    data: periodPresences,
    isLoading: isLoadingHistoryRows,
    error: historyRowsError,
    refetch: refetchHistoryRows,
  } = useQuery({
    queryKey: housePresenceKeys.history(houseId, appliedRange),
    queryFn: () =>
      houseService.listPeriodPresences(houseId, {
        date_from: appliedRange.dateFrom,
        date_to: appliedRange.dateTo,
      }),
  });

  const applyRange = () => {
    setAppliedRange({ dateFrom, dateTo });
  };

  const resetRange = () => {
    const defaults = defaultDateRange();
    setDateFrom(defaults.dateFrom);
    setDateTo(defaults.dateTo);
    setAppliedRange(defaults);
  };

  const historyItems = useMemo(
    () => periodPresences ?? [],
    [periodPresences],
  );
  const metrics = useMemo(
    () => buildPresenceMetrics(historyItems, appliedRange, deviceCount),
    [historyItems, appliedRange, deviceCount],
  );

  const invalidatePresenceLists = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: housePresenceKeys.current(houseId),
      }),
      queryClient.invalidateQueries({
        queryKey: housePresenceKeys.upcoming(houseId),
      }),
      queryClient.invalidateQueries({
        queryKey: housePresenceKeys.history(houseId),
      }),
      queryClient.invalidateQueries({
        queryKey: ["kiosk-presences", houseId],
      }),
      queryClient.invalidateQueries({
        queryKey: deviceKeys.byHouse(houseId),
      }),
    ]);
  };

  const deleteMutation = useMutation({
    mutationFn: (presence: HouseCurrentPresenceItem) =>
      houseService.deleteDevicePresence(
        houseId,
        presence.device_id,
        presence.id,
      ),
    onSuccess: async (_result, presence) => {
      await invalidatePresenceLists();
      setPendingDelete(null);
      setEditingPresence((current) =>
        current?.id === presence.id ? null : current,
      );
      toast.success(t("kioskPresence.toastDeleted"));
    },
    onError: (err: unknown) => {
      toastApiError(err, "deletePresence");
    },
  });

  const editingDevice = editingPresence
    ? houseDevices.find((device) => device.id === editingPresence.device_id)
    : undefined;

  const staysTitle = (
    <DataTableTitle
      icon={Users}
      description={
        staysView === "current"
          ? t("detail.presences.currentHint")
          : t("detail.presences.upcomingHint")
      }
    >
      {t("detail.presences.staysTitle")}
    </DataTableTitle>
  );
  const staysToggle = (
    <ToggleGroup
      variant="segmented"
      size="sm"
      ariaLabel={t("detail.presences.segmentAria")}
      value={staysView}
      onChange={setStaysView}
      options={[
        {
          value: "current",
          label: t("detail.presences.segmentCurrent", {
            count: currentPresences.length,
          }),
          icon: LogIn,
        },
        {
          value: "upcoming",
          label: t("detail.presences.segmentUpcoming", {
            count: upcomingPresences.length,
          }),
          icon: CalendarRange,
        },
      ]}
    />
  );

  return (
    <div className="space-y-8">
      {editingPresence && editingDevice ? (
        <EditHousePresenceModal
          key={editingPresence.id}
          houseId={houseId}
          presence={editingPresence}
          regionCode={editingDevice.region_code}
          onClose={() => setEditingPresence(null)}
          onDeleted={() => {
            setEditingPresence(null);
            void invalidatePresenceLists();
          }}
        />
      ) : null}

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        isOpen={pendingDelete != null}
        onClose={() => {
          if (deleteMutation.isPending) return;
          setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete);
        }}
        title={tCommon("delete")}
        message={t("kioskPresence.deleteConfirm", {
          guestName: presenceGuestDisplayName(
            pendingDelete?.guest_name,
            t("detail.presences.genericGuestName"),
          ),
        })}
        confirmText={tCommon("delete")}
        isDestructive
        isLoading={deleteMutation.isPending}
      />

      <section className="animate-slide-up">
        {staysView === "current" ? (
          <PresenceTable
            key="current"
            title={staysTitle}
            actions={staysToggle}
            preferencesKey="house-presences-current"
            presences={currentPresences}
            emptyMessage={t("detail.presences.emptyCurrentMessage")}
            loadState={
              isLoadingCurrent
                ? { status: "loading" }
                : currentError
                  ? {
                      status: "error",
                      message: t("detail.presences.errorConnectionMessage"),
                      retryLabel: tCommon("tryAgain"),
                      onRetry: () => void refetchCurrent(),
                    }
                  : undefined
            }
            onEdit={canEditPresences ? setEditingPresence : undefined}
            onDelete={canDeletePresences ? setPendingDelete : undefined}
          />
        ) : (
          <PresenceTable
            key="upcoming"
            title={staysTitle}
            actions={staysToggle}
            preferencesKey="house-presences-upcoming"
            presences={upcomingPresences}
            emptyMessage={t("detail.presences.emptyUpcomingMessage")}
            loadState={
              isLoadingUpcoming
                ? { status: "loading" }
                : upcomingError
                  ? {
                      status: "error",
                      message: t("detail.presences.errorConnectionMessage"),
                      retryLabel: tCommon("tryAgain"),
                      onRetry: () => void refetchUpcoming(),
                    }
                  : undefined
            }
            defaultSortDirection="asc"
            onEdit={canEditPresences ? setEditingPresence : undefined}
            onDelete={canDeletePresences ? setPendingDelete : undefined}
          />
        )}
      </section>

      <section className="animate-slide-up space-y-5">
        <Card className="overflow-hidden p-0">
          <div className="flex flex-col gap-4 border-b border-surface-200 px-5 py-4">
            <div className="min-w-0">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold tracking-tight text-surface-900">
                <BarChart3 className="h-4 w-4 text-surface-500" aria-hidden />
                {t("detail.presences.historyHeading")}
              </h3>
              <p className="mt-0.5 text-[13px] text-surface-500">
                {t("detail.presences.historyHint")}
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <WalletDateRangeInputs
                startDate={dateFrom}
                endDate={dateTo}
                clampToToday={false}
                startLabel={t("detail.presences.dateFrom")}
                endLabel={t("detail.presences.dateTo")}
                className="flex flex-wrap items-end gap-2"
                inputClassName="min-w-[9.5rem]"
                onChange={({ startDate, endDate }) => {
                  setDateFrom(startDate);
                  setDateTo(endDate);
                }}
              />
              <Button type="button" onClick={applyRange}>
                {t("detail.presences.applyRange")}
              </Button>
              <Button type="button" variant="outline" onClick={resetRange}>
                {t("detail.presences.resetRange")}
              </Button>
            </div>
          </div>

          <div className="px-5 py-4">
            {isLoadingHistoryRows ? (
              <LoadingState message={t("detail.presences.loadingHistory")} />
            ) : historyRowsError ? (
              <ErrorState
                title={t("detail.presences.errorHistoryTitle")}
                message={t("detail.presences.errorConnectionMessage")}
                onRetry={() => refetchHistoryRows()}
              />
            ) : (
              <div className="grid gap-5 lg:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
                <dl className="grid grid-cols-3 gap-3 lg:grid-cols-1">
                  {[
                    {
                      key: "occupancy",
                      label: t("detail.presences.metricOccupancy"),
                      value: formatPercent(metrics.occupancy_percentage),
                      hint: t("detail.presences.metricOccupancyHint"),
                      icon: BarChart3,
                      tone: "bg-primary-50 text-primary-600 ring-primary-600/15",
                    },
                    {
                      key: "devices",
                      label: t("detail.presences.metricDevices"),
                      value: String(metrics.device_count),
                      icon: Users,
                      tone: "bg-accent-50 text-accent-600 ring-accent-600/15",
                    },
                    {
                      key: "presences",
                      label: t("detail.presences.metricPresences"),
                      value: String(metrics.presence_count),
                      icon: CalendarRange,
                      tone: "bg-success-50 text-success-600 ring-success-600/15",
                    },
                  ].map(({ key, label, value, hint, icon: Icon, tone }) => (
                    <div
                      key={key}
                      className="flex min-w-0 items-start gap-3 rounded-lg border border-surface-200 bg-surface-50 px-3.5 py-3"
                    >
                      <span
                        className={`hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset sm:flex ${tone}`}
                        aria-hidden
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <dt className="truncate text-xs font-medium text-surface-500">
                          {label}
                        </dt>
                        <dd
                          className="mt-0.5 text-xl font-semibold tracking-tight text-surface-900 tabular-nums"
                          title={hint}
                        >
                          {value}
                        </dd>
                      </div>
                    </div>
                  ))}
                </dl>

                <div className="min-w-0">
                  <h4 className="mb-2.5 text-xs font-medium text-surface-500">
                    {t("detail.presences.chartHeading")}
                  </h4>
                  {metrics.presence_count === 0 ? (
                    <EmptyState
                      variant="dashed"
                      size="sm"
                      icon={BarChart3}
                      title={t("detail.presences.emptyHistoryMessage")}
                    />
                  ) : (
                    <PresenceOccupancyChart
                      buckets={metrics.daily_buckets}
                      ariaLabel={t("detail.presences.chartAria")}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>

        {!isLoadingHistoryRows && !historyRowsError ? (
          <div>
            <PresenceTable
              title={
                <DataTableTitle
                  as="h4"
                  description={t("detail.presences.periodTableHint")}
                >
                  {t("detail.presences.periodTableHeading")}
                </DataTableTitle>
              }
              preferencesKey="house-presences-history"
              presences={historyItems}
              emptyMessage={t("detail.presences.emptyPeriodMessage")}
              showCurrentIndicator
              resetToken={`${appliedRange.dateFrom}|${appliedRange.dateTo}`}
              onEdit={canEditPresences ? setEditingPresence : undefined}
              onDelete={canDeletePresences ? setPendingDelete : undefined}
            />
          </div>
        ) : null}
      </section>
    </div>
  );
}
